import { Link, useNavigate } from 'react-router-dom';
import { User, MapPin, Lock, Package, LogOut, ShieldCheck, Database } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export const ACCOUNT_TABS = [
  { id: 'orders', label: 'My Orders', icon: Package },
  { id: 'profile', label: 'Personal Details', icon: User },
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'security', label: 'Password', icon: Lock },
];

/**
 * Shared sidebar for the account area.
 * - On AccountPage, pass `activeTab` + `onTabChange` to switch the page's local tab.
 * - On other pages (e.g. order detail), omit `onTabChange` and tabs become
 *   <Link>s that navigate to /account?tab=...
 */
const AccountSidebar = ({ activeTab, onTabChange }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="sm:w-56 flex-shrink-0" data-testid="account-sidebar">
      <div className="relative sm:static">
        <div className="bg-white rounded-xl shadow-card p-2 flex sm:flex-col gap-1 overflow-x-auto scrollbar-hide">
          {user?.is_admin && (
            <Link
              to="/admin/scraper"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap text-gray-600 hover:bg-gray-50 transition-colors"
              data-testid="sidebar-scraper-link"
            >
              <Database className="w-4 h-4" />Scrapers
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap text-gray-600 hover:bg-gray-50 transition-colors"
              data-testid="sidebar-admin-link"
            >
              <ShieldCheck className="w-4 h-4" />Admin Area
            </Link>
          )}
          {user?.is_admin && <div className="hidden sm:block border-t border-gray-100 my-1" />}
          {ACCOUNT_TABS.map((t) => {
            const isActive = activeTab === t.id;
            const className = `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              isActive ? 'bg-bush/10 text-bush' : 'text-gray-600 hover:bg-gray-50'
            }`;
            // When parent provides an onTabChange handler we treat the sidebar as
            // "internal" tabs (AccountPage). Otherwise navigate to /account?tab=X.
            return onTabChange ? (
              <button
                key={t.id}
                onClick={() => onTabChange(t.id)}
                className={className}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ) : (
              <Link
                key={t.id}
                to={`/account?tab=${t.id}`}
                className={className}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />{t.label}
              </Link>
            );
          })}
          <div className="hidden sm:block border-t border-gray-100 my-1" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap text-gray-600 hover:bg-gray-50 transition-colors"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4" />Logout
          </button>
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none rounded-r-xl sm:hidden" />
      </div>
    </div>
  );
};

export default AccountSidebar;
